import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

export default function AdminCalendarTypes() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ name:"", colour:"#16a34a", icon:"", resource_source:"none" });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/calendar-types"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const open = (t) => { setEditing(t); setForm(t ? { name:t.name, colour:t.colour, icon:t.icon||"", resource_source:t.resource_source } : { name:"", colour:"#16a34a", icon:"", resource_source:"none" }); setFormOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/calendar-types/${editing.id}`, form);
      else await api.post("/admin/calendar-types", form);
      toast.success(editing ? "Type updated" : "Type created");
      setFormOpen(false); load();
    } catch (err) { toast.error(formatApiError(err) || "Save failed"); }
  };
  const doDelete = async () => { const t = confirm; setConfirm(null); if (!t) return;
    try { await api.delete(`/admin/calendar-types/${t.id}`); toast.success("Type deleted"); load(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <div data-testid="admin-calendar-types" className="mx-auto w-full max-w-5xl">
      <Breadcrumbs />
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="font-display text-2xl font-semibold text-slate-900">Calendar Types</h2>
          <p className="mt-1 text-sm text-slate-500">{rows.length} type{rows.length===1?"":"s"} · colour and resource binding per type.</p></div>
        <Button onClick={() => open(null)} data-testid="types-create-btn" className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"><Plus className="h-4 w-4" />New type</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table><TableHeader><TableRow>
          <TableHead className="pl-4">Name</TableHead><TableHead>Colour</TableHead>
          <TableHead>Icon</TableHead><TableHead>Resource source</TableHead>
          <TableHead>Used by</TableHead><TableHead className="pr-4 text-right">Actions</TableHead>
        </TableRow></TableHeader><TableBody>
          {loading && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell></TableRow>}
          {rows.map(t => (
            <TableRow key={t.id} data-testid={`type-row-${t.id}`}>
              <TableCell className="pl-4 font-medium">{t.name}</TableCell>
              <TableCell><div className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-md border border-slate-200" style={{background:t.colour}} /><code className="text-[11px]">{t.colour}</code></div></TableCell>
              <TableCell className="font-mono text-xs">{t.icon || "—"}</TableCell>
              <TableCell><code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{t.resource_source}</code></TableCell>
              <TableCell><span className="font-mono text-sm">{t.used_by_count}</span></TableCell>
              <TableCell className="pr-4 text-right"><div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => open(t)} data-testid={`type-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setConfirm(t)} data-testid={`type-delete-${t.id}`} className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"><Trash2 className="h-4 w-4" /></Button>
              </div></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="type-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit type" : "New type"}</DialogTitle>
            <DialogDescription>Choose a colour and the resource that entries of this type reference.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2"><Label>Name</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="type-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Colour</Label><Input type="color" value={form.colour} onChange={e=>setForm({...form,colour:e.target.value})} data-testid="type-form-colour" className="h-10" /></div>
              <div className="space-y-2"><Label>Icon name</Label><Input value={form.icon} onChange={e=>setForm({...form,icon:e.target.value})} data-testid="type-form-icon" placeholder="e.g. truck" /></div>
            </div>
            <div className="space-y-2"><Label>Resource source</Label>
              <Select value={form.resource_source} onValueChange={v => setForm({...form,resource_source:v})}>
                <SelectTrigger data-testid="type-form-resource-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicles">Vehicles</SelectItem>
                  <SelectItem value="users">Users</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="type-form-submit" className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete "{confirm?.name}"?</AlertDialogTitle>
          <AlertDialogDescription>This is blocked if any entries use this type.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
